/**
 * Whether the point `(x, y)` (schema canvas units) lies on the SVG path `d`
 * — inside its filled outline or within `tolerance` of its edge (`0`: inside
 * only).
 */
export type SvgPathContains = (d: string, x: number, y: number, tolerance: number) => boolean

/**
 * Browser implementation over `Path2D` + a detached 2D context, with one
 * parsed `Path2D` cached per path string (a map holds a few thousand). The
 * canvas units of the schema are used as-is: the context is never drawn to,
 * only asked `isPointInPath` / `isPointInStroke`. Where `Path2D` or a 2D
 * context is missing (jsdom, an old engine) nothing is ever hit — a segment
 * tap degrades to an empty-area tap.
 */
export function createCanvasSvgPathContains(): SvgPathContains {
  const context =
    typeof Path2D === 'undefined' || typeof document === 'undefined'
      ? null
      : document.createElement('canvas').getContext('2d')
  if (context === null) return () => false
  const cache = new Map<string, Path2D>()
  /* v8 ignore start -- Path2D hit tests need a real canvas (Playwright covers them) */
  return (d, x, y, tolerance) => {
    let path = cache.get(d)
    if (path === undefined) {
      path = new Path2D(d)
      cache.set(d, path)
    }
    if (context.isPointInPath(path, x, y)) return true
    if (tolerance <= 0) return false
    context.lineWidth = tolerance * 2
    return context.isPointInStroke(path, x, y)
  }
  /* v8 ignore stop */
}
